import type { RevenueBasis, RevenueBreakdown } from '@/api/revenue'
import { Card, EmptyState, LoadingBlock, type FormatOptions } from '@/design'
import { CardQueryBody } from '@/features/overview/CardQueryBody'
import { formatDateKey, type GlobalFilters } from '@/lib/filters'

import { BASIS_NOUN, NULL_REASON_TEXT, rangeLabel } from '../format'
import { PanelNote } from '../pieces'
import { MAX_WEEKS, useRevenueBreakdown, useWeeklyBreakdowns, weekWindows, type WeekWindow, type WeeklyBreakdown } from '../queries'
import { NARROW_WIDTH, StackedColumns, type StackColumn, type StackSeries } from './StackedColumns'

/** The channels the weekly stack names; every other row is one folded segment. */
export const TOP_CHANNELS = 3
const OTHER_KEY = '__other'
/**
 * The folded segment's name: every other channel and the rows that are no channel at all
 * (unattributed, not classified yet, refunds of unsynced orders). Never just "Other", which
 * is also a channel of the taxonomy and can be one of the three named.
 */
export const FOLDED_LABEL = 'All other, incl. unattributed'

/** Every channel a week can hold, so its "Other" sums every row but the named ones (a total for display, not a figure). */
const ALL_ROWS = 50

/** The customer types, always these three in this order (the API's), each its own slot. */
const CUSTOMER_SERIES: readonly StackSeries[] = [
  { key: 'new', label: 'New', slot: 1 },
  { key: 'returning', label: 'Returning', slot: 2 },
  { key: 'guest', label: 'Guest', slot: 3 },
]

/**
 * Under net, refunds of orders that never synced are a row of their own with a negative
 * share: listed (readout, table, legend) whenever a week has one, never drawn, so the
 * three types' shares may add up to more than 100%.
 */
const UNMATCHED_REFUNDS: StackSeries = { key: 'unmatched_refunds', label: 'Refunds of orders not synced (not drawn)', slot: 4, listedOnly: true }

function customerSeries(breakdowns: readonly (RevenueBreakdown | undefined)[]): readonly StackSeries[] {
  const unmatched = breakdowns.some((breakdown) => breakdown?.rows.some((row) => row.kind === 'unmatched_refunds'))
  return unmatched ? [...CUSTOMER_SERIES, UNMATCHED_REFUNDS] : CUSTOMER_SERIES
}

function weekLabels(week: WeekWindow) {
  return { label: formatDateKey(week.from), title: rangeLabel(week) }
}

/** Weekly while the range is at most `MAX_WEEKS` weeks; past that, one column for the whole period. */
function weeksFor(range: { from: string; to: string }): { weeks: WeekWindow[]; tooLong: boolean } {
  const weeks = weekWindows(range)
  return weeks.length > MAX_WEEKS ? { weeks: [], tooLong: true } : { weeks, tooLong: false }
}

function TooLongNote() {
  return <PanelNote>The weekly split is shown for ranges up to {MAX_WEEKS} weeks; this is the whole period.</PanelNote>
}

/** A column from one breakdown: the named series' rows, and "Other" as the sum of every other row. */
function channelColumn(key: string, labels: { label: string; title: string }, breakdown: RevenueBreakdown, named: readonly StackSeries[]): StackColumn {
  const values: Record<string, number | null> = {}
  const keys = new Set(named.map((item) => item.key))
  for (const item of named) values[item.key] = breakdown.rows.find((row) => row.kind === 'value' && row.key === item.key)?.revenue ?? 0
  values[OTHER_KEY] = breakdown.rows.filter((row) => !(row.kind === 'value' && keys.has(row.key))).reduce((sum, row) => sum + row.revenue, 0)
  return { key, ...labels, values }
}

/**
 * Revenue by channel, week by week (last non-direct, the channel summary's default): the
 * period's top three channels and everything else, in fixed slots, from one breakdown per
 * week so every figure is the API's.
 */
export function ChannelWeeklyPanel({
  filters,
  basis,
  range,
  money,
}: {
  filters: GlobalFilters
  basis: RevenueBasis
  /** The period the page reads: the filters' own, which the API is sent. */
  range: { from: string; to: string }
  money: FormatOptions
}) {
  const period = useRevenueBreakdown(filters, basis, 'channel', { limit: ALL_ROWS })
  const { weeks, tooLong } = weeksFor(range)
  const weekly = useWeeklyBreakdowns(filters, basis, 'channel', weeks, ALL_ROWS)

  return (
    <Card className="rv-panel rv-panel--wide-half" title="By channel, weekly" subtitle={`Last non-direct · ${BASIS_NOUN[basis]}`} busy={period.isFetching || weekly.isFetching}>
      <CardQueryBody query={period} loadingRows={5} errorDescription="Revenue by channel could not be loaded.">
        {({ data }) => {
          const top = data.rows.filter((row) => row.kind === 'value' && row.revenue > 0).slice(0, TOP_CHANNELS)
          const series: StackSeries[] = [
            ...top.map((row, index) => ({ key: row.key, label: row.label, slot: (index + 1) as StackSeries['slot'] })),
            { key: OTHER_KEY, label: FOLDED_LABEL, slot: 4 },
          ]
          if (tooLong) {
            return (
              <>
                <StackedColumns title="Revenue by channel" series={series} mode="amount" money={money} periodHeader="Period" columns={[channelColumn('period', { label: 'Period', title: rangeLabel(range) }, data, series)]} />
                <TooLongNote />
              </>
            )
          }
          return <WeeklyBody weekly={weekly} build={(week) => (week.data ? channelColumn(week.week.from, weekLabels(week.week), week.data, series) : null)} series={series} title="Revenue by channel, weekly" mode="amount" money={money} />
        }}
      </CardQueryBody>
    </Card>
  )
}

function customerColumn(key: string, labels: { label: string; title: string }, breakdown: RevenueBreakdown): StackColumn {
  const values: Record<string, number | null> = {}
  for (const item of [...CUSTOMER_SERIES, UNMATCHED_REFUNDS]) values[item.key] = breakdown.rows.find((row) => row.key === item.key)?.share ?? null
  return {
    key,
    ...labels,
    values,
    note: breakdown.shareNullReason ? NULL_REASON_TEXT[breakdown.shareNullReason] : undefined,
  }
}

/**
 * Each week's revenue split between new, returning and guest customers, as the API's own
 * shares of that week (a 100% stack). Under net, refunds of orders that never synced are a
 * row of their own and are not drawn.
 */
export function CustomerMixPanel({
  filters,
  basis,
  range,
  money,
}: {
  filters: GlobalFilters
  basis: RevenueBasis
  range: { from: string; to: string }
  money: FormatOptions
}) {
  const { weeks, tooLong } = weeksFor(range)
  const period = useRevenueBreakdown(filters, basis, 'customer_type', { enabled: tooLong })
  const weekly = useWeeklyBreakdowns(filters, basis, 'customer_type', weeks)
  return (
    <Card className="rv-panel" title="New vs returning" subtitle={`Share of ${BASIS_NOUN[basis]}, weekly`} busy={weekly.isFetching || period.isFetching}>
      {tooLong ? (
        <CardQueryBody query={period} loadingRows={4} errorDescription="Revenue by customer type could not be loaded.">
          {({ data }) => (
            <>
              <StackedColumns title="Share of revenue by customer type" series={customerSeries([data])} mode="share" money={money} width={NARROW_WIDTH} periodHeader="Period" columns={[customerColumn('period', { label: 'Period', title: rangeLabel(range) }, data)]} />
              <TooLongNote />
            </>
          )}
        </CardQueryBody>
      ) : (
        <WeeklyBody weekly={weekly} build={(week) => (week.data ? customerColumn(week.week.from, weekLabels(week.week), week.data) : null)} series={customerSeries(weekly.weeks.map((week) => week.data))} title="Share of revenue by customer type, weekly" mode="share" money={money} width={NARROW_WIDTH} />
      )}
    </Card>
  )
}

function WeeklyBody({
  weekly,
  build,
  series,
  title,
  mode,
  money,
  width,
}: {
  weekly: { weeks: WeeklyBreakdown[]; isPending: boolean; isError: boolean }
  build: (week: WeeklyBreakdown) => StackColumn | null
  series: readonly StackSeries[]
  title: string
  mode: 'amount' | 'share'
  money: FormatOptions
  width?: number
}) {
  if (weekly.isError) return <EmptyState variant="outage" compact description="A week’s figures could not be loaded." />
  if (weekly.weeks.length === 0 || weekly.isPending) return <LoadingBlock rows={5} />
  const columns = weekly.weeks.map(build).filter((column): column is StackColumn => column !== null)
  return <StackedColumns title={title} series={series} columns={columns} mode={mode} money={money} width={width} />
}
